import { STEP_NAME, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, DateCalendar, Grid, NoSsr, ToggleButton, ToggleButtonGroup, Typography } from '../../lib/base';
import { ExpandoCard } from '../common/card';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { getTaskTimesForCrop, startDateToOrderDate } from './utils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface CropTimelineCardProps {
  readonly totalGrowTime: number;
  readonly bufferTime: number;
  readonly steps: readonly { readonly name: string; readonly time_to_start_after_first_step: number }[];
}

export function CropTimelineCard({ totalGrowTime, bufferTime, steps }: CropTimelineCardProps) {
  const { timezone } = useUserContext();
  const initialStartTime = dayjs
    .unix(
      startDateToOrderDate({
        startDate: utils.todayStart(timezone).hour(12).unix(),
        totalGrowTime,
        bufferTime,
      }),
    )
    .tz(timezone);
  const [orderOrStartTime, setOrderOrStateTime] = React.useState<dayjs.Dayjs>(initialStartTime);
  const [dateIsOrderDate, setDateIsOrderDate] = React.useState<boolean>(false);

  const onChangeFullfillmentTime = (newValue: dayjs.Dayjs | null) => {
    setOrderOrStateTime(newValue ?? utils.todayStart(timezone));
  };
  const onChangeOrderOrPlantDate = (e: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue == null) {
      return;
    }
    setDateIsOrderDate(newValue);
  };

  const calendarPicker = <DateCalendar value={orderOrStartTime} onChange={onChangeFullfillmentTime} />;
  const datePicker = (
    <InternalDatePicker
      label={`${dateIsOrderDate ? 'Delivery' : 'Start'} Date *`}
      value={orderOrStartTime}
      onChange={onChangeFullfillmentTime}
      textFieldProps={{
        fullWidth: true,
      }}
    />
  );

  return (
    <ExpandoCard
      title="Grow Timeline Calculator"
      content={
        <Grid spacing={2} sx={{ p: 2 }} container size={{ xs: 12 }} justifyContent="center">
          <Grid sx={{ display: { xs: 'none', md: 'block' } }} size={{ xs: 12, md: 6 }}>
            <NoSsr>{calendarPicker}</NoSsr>
          </Grid>
          <Grid sx={{ display: { xs: 'block', md: 'none' } }} size={{ xs: 12, md: 6 }}>
            <NoSsr>{datePicker}</NoSsr>
          </Grid>
          <Grid
            container
            flexDirection="column"
            alignItems={{ xs: 'flex-start', sm: 'center', md: 'flex-start' }}
            sx={{ p: 1 }}
            spacing={1}
            size={{ xs: 12, md: 6 }}
          >
            <Grid>
              <Typography variant="body2">{`Selected date is the ${
                dateIsOrderDate ? 'order' : 'start'
              } date`}</Typography>
            </Grid>
            <Grid>
              <ToggleButtonGroup
                size="small"
                value={dateIsOrderDate}
                exclusive
                onChange={onChangeOrderOrPlantDate}
                aria-label="select-toggle-date"
              >
                <ToggleButton value={false} aria-label="left aligned">
                  Start Date
                </ToggleButton>
                <ToggleButton value={true} aria-label="right aligned">
                  Order Date
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid>
              <CropTimelineCalculator
                orderTime={
                  dateIsOrderDate
                    ? orderOrStartTime.unix()
                    : startDateToOrderDate({ startDate: orderOrStartTime.unix(), totalGrowTime, bufferTime })
                }
                totalGrowTime={totalGrowTime}
                bufferTime={bufferTime}
                steps={steps}
              />
            </Grid>
          </Grid>
        </Grid>
      }
    />
  );
}

interface Props extends CropTimelineCardProps {
  readonly orderTime: number;
}

function CropTimelineCalculator({ orderTime, totalGrowTime, bufferTime, steps }: Props) {
  const { timezone } = useUserContext();
  const result = getTaskTimesForCrop({ totalGrowTime, bufferTime, orderDate: orderTime, steps });

  const dateFormat = 'ddd, MMM D';
  const startDate = dayjs.unix(result.startTime).tz(timezone).format(dateFormat);
  const harvestDate = dayjs.unix(result.harvestTime).tz(timezone).format(dateFormat);
  const taskTimes = result.taskTimes.map((t) => ({
    ...t,
    time: dayjs.unix(t.time).tz(timezone).format(dateFormat),
  }));
  const orderTimeFormatted = dayjs.unix(orderTime).tz(timezone).format(dateFormat);
  const secondsToDays = (seconds: number) => Math.round(seconds / 60 / 60 / 24);

  return (
    <Box>
      <Typography variant="h6">Timeline</Typography>
      <Typography variant="body2">Start: {startDate}</Typography>
      <ul>
        {taskTimes.map((t) => {
          if (t.name === STEP_NAME.HARVEST) {
            return null;
          }
          return (
            <li key={t.name + t.time}>
              <Typography variant="body2">
                {t.name}: {t.time}
              </Typography>
              {t.duration > 0 && (
                <Typography variant="body2">Duration: {utils.round(secondsToDays(t.duration), 10)} days</Typography>
              )}
            </li>
          );
        })}
      </ul>
      <Typography variant="body2">Harvest: {harvestDate}</Typography>
      <Typography variant="body2">Order Delivery: {orderTimeFormatted}</Typography>
    </Box>
  );
}
